import React, {useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import IssueNewModalContainer from './components/project-view/IssueNewModalContainer';
import BaseComponentContainer from './components/BaseComponentContainer';
import {initialFieldValuesType} from './constants/proptype-validation';
import {useMappedInitialFieldValues} from './utils/issueCreate-helpers';
import featureFlags, {FEATURE_FLAGS} from './utils/featureFlags';

const IssueCreateModal = ({
  initialFieldValues,
  onSuccess,
  isOpen,
  onClose,
  additionalAttributes,
  localStoragePrefix,
  defaultIssueSubtypeId,
  availableStatuses,
  container,
  className
}) => {
  const mappedInitialFieldValues = useMappedInitialFieldValues(initialFieldValues);
  const baseRef = useRef();
  const handleOnSuccess = useCallback(({response}) => onSuccess({response}), [onSuccess]);

  return (
    <BaseComponentContainer
      disabled={!featureFlags.get(FEATURE_FLAGS.RESTRICT_CREATE_ISSUE_DISABLE_PROP) && !isOpen}
      ref={baseRef}
    >
      <IssueNewModalContainer
        container={container ?? baseRef.current}
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={handleOnSuccess}
        additionalAttributes={additionalAttributes}
        initialFieldValues={mappedInitialFieldValues}
        localStoragePrefix={localStoragePrefix}
        defaultIssueSubtypeId={defaultIssueSubtypeId}
        availableStatuses={availableStatuses}
        className={className}
      />
    </BaseComponentContainer>
  );
};

IssueCreateModal.displayName = 'IssueCreateModal';

IssueCreateModal.propTypes = {
  /**
   * Whether or not the modal is open
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * Callback when closing the modal
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Callback called after issue create success.
   * The handler is of the shape of "({response}) => *"
   */
  onSuccess: PropTypes.func,
  /**
   * Additional attributes passed to issues BE on create request.
   */
  additionalAttributes: PropTypes.object,
  /**
   * An object containing the initial values for create form fields, in the form of {field: value} pairs.
   */
  initialFieldValues: initialFieldValuesType,
  /**
   * Prefix to append to the localStorage keys used by Issue-Create components
   */
  localStoragePrefix: PropTypes.string,
  /**
   * Default subtypeId - this will take affect with the lowest precedence.
   */
  defaultIssueSubtypeId: PropTypes.string,
  /**
   * availableStatuses - An array of statuses to restrict.
   */
  availableStatuses: PropTypes.array,
  /**
   * create modal container.
   */
  container: PropTypes.object,
  /**
   * classname to be applied on create modal.
   */
  className: PropTypes.string
};

export default IssueCreateModal;
